// Admin · Accounts (Lead-only). Add & manage teacher accounts from the UI so teacher churn no longer
// needs SQL re-seeding. List (name · email · role · active) → Add teacher · Reset passcode ·
// Deactivate/Reactivate · change role. Calm + one-thumb: account state is coordination/admin, never a
// score. A passcode is a credential — it's set here but NEVER shown back. Deactivating keeps history.

import { api } from "../api";
import { esc } from "../dom";
import { me } from "../state";
import { fileToCappedDataUrl } from "../blocks";
import { icon } from "../icons";
import { pageHeader } from "../ui";
import { mountClassManager } from "./class-manager";

interface AdminUser {
  id: string;
  name: string;
  email: string;
  auth_role: "teacher" | "lead";
  coaching_posture: "junior" | "regular" | "senior" | null;
  active: boolean;
}

interface AdminTerm {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  week_count: number;
  ay: string | null;
}

const SECTIONS = [
  { id: "people", label: "People" },
  { id: "classes", label: "Classes" },
  { id: "year", label: "School year" },
  { id: "printing", label: "Printing" },
  { id: "ai", label: "AI" },
] as const;

export async function renderAdmin(app: HTMLElement, _params: Record<string, string> = {}, query?: URLSearchParams): Promise<void> {
  const meId = me()?.id;
  const tab = query?.get("tab") ?? "";
  let flash = ""; // a calm one-shot confirmation, re-rendered once by load()
  // The term row just saved: the list re-sorts by date after a save, so the row can move — it is
  // marked "Saved" and scrolled back into view instead of leaving the Lead to find it.
  let savedTermId: string | null = null;
  // the class table fills in after the page (its own fetch) and pushes the School year section down —
  // the scroll back to a saved term row waits for it
  let classesReady: Promise<unknown> = Promise.resolve();

  // Coaching posture = pedagogy (the JD tier: junior/regular/senior), NOT a role. It sets the
  // default coaching stance for the dialogue (Phase 1c) — junior gets guided help, senior pure
  // inquiry. Shown and set here so a new teacher lands with the right posture from day one.
  const POSTURES = ["junior", "regular", "senior"] as const;
  const postureSelect = (current: AdminUser["coaching_posture"]): string => {
    const opts =
      `<option value=""${current ? "" : " selected"}>no level</option>` +
      POSTURES.map((p) => `<option value="${p}"${p === current ? " selected" : ""}>${p}</option>`).join("");
    return `<select class="proj-move" data-act="posture" aria-label="Coaching posture">${opts}</select>`;
  };

  const rowHtml = (u: AdminUser): string => {
    const self = u.id === meId;
    const stateChip = u.active ? "" : ` <span class="chip">inactive</span>`;
    // A Lead can't deactivate or re-role their OWN account (no self-lockout) — only reset its passcode.
    const toggle = self
      ? ""
      : `<a href="#" data-act="toggle" class="muted">${u.active ? "Deactivate" : "Reactivate"}</a>`;
    const roleAct = self
      ? ""
      : `<a href="#" data-act="role" class="muted">Make ${u.auth_role === "lead" ? "teacher" : "lead"}</a>`;
    return `<div class="user-row${u.active ? "" : " removed"}" data-id="${esc(u.id)}" data-active="${
      u.active ? 1 : 0
    }" data-role="${esc(u.auth_role)}">
        <div class="ur-who">
          <span class="avatar" aria-hidden="true">${esc(u.name.slice(0, 1).toUpperCase())}</span>
          <span><strong class="uname">${esc(u.name)}</strong>
          <span class="muted small ur-email">${esc(u.email)}${self ? " · you" : ""}</span></span>
        </div>
        <div class="ur-role"><span class="chip">${esc(u.auth_role)}</span>${stateChip}</div>
        <div class="row-actions ur-actions">
          <a href="#" data-act="reset" class="muted">Reset passcode</a>
          ${toggle}
          ${roleAct}
          ${postureSelect(u.coaching_posture)}
        </div>
      </div>`;
  };

  // Academic year (0018) — three terms, typed in by the Lead; dates come from the school's own
  // calendar document, never generated or guessed here (CLAUDE.md §3). Each row is its own small
  // always-editable form — no separate "edit mode": tweak a field, Save, calm confirmation.
  const termRow = (term: AdminTerm): string =>
    `<form class="term-row card${term.id === savedTermId ? " just-saved" : ""}" data-id="${esc(term.id)}">
      <div class="form-grid term-grid">
        <label>Term name<input name="name" value="${esc(term.name)}" required aria-label="Term name" placeholder="Term name" /></label>
        <label>Year label<input name="ay" value="${esc(term.ay ?? "")}" aria-label="Academic year label" placeholder="AY2026/27" /></label>
        <label>Starts<input type="date" name="start_date" value="${esc(term.start_date)}" required aria-label="Start date" /></label>
        <label>Ends<input type="date" name="end_date" value="${esc(term.end_date)}" required aria-label="End date" /></label>
        <label>Weeks<input type="number" name="week_count" value="${term.week_count}" min="1" max="20" required aria-label="Week count" /></label>
        <div class="form-actions"><button class="btn sm" type="submit">Save</button>${term.id === savedTermId ? `<span class="saved-mark" role="status">${icon("check", "ico-sm")}Saved</span>` : ""}</div>
      </div>
    </form>`;

  async function load(): Promise<void> {
    const [{ users }, { settings }, { terms }, drift] = await Promise.all([
      api<{ users: AdminUser[] }>("/api/admin/users"),
      api<{ settings: Record<string, { value: string; updated_at: string }> }>("/api/admin/settings"),
      api<{ terms: AdminTerm[] }>("/api/terms"),
      api<{ drifted: number; total: number }>("/api/admin/deadline-drift"),
    ]);
    const tier2Model = settings["tier2_model"]?.value ?? "";
    const schoolNameEn = settings["school_name_en"]?.value ?? "";
    const schoolNameKm = settings["school_name_km"]?.value ?? "";
    const schoolLogo = settings["school_logo"]?.value ?? "";
    const jump = `<nav class="jump setup-jump" aria-label="Setup sections">${SECTIONS.map((x) => `<a href="#${x.id}" data-jump="${x.id}">${x.label}</a>`).join("")}</nav>`;
    app.innerHTML = `
      ${pageHeader({ title: "Setup", sub: "People, classes and the school year. Changes here reach everyone's screens straight away." })}
      ${jump}
      ${flash ? `<div class="toast">${esc(flash)}</div>` : ""}
      <div id="err" class="error" hidden></div>

      <section class="setup-sec" id="people">
        <div class="setup-head"><h2>People</h2><span class="muted small">${users.filter((x) => x.active).length} active accounts</span></div>
        <p class="hint">Add and manage accounts. Deactivating keeps a teacher's history — it only stops sign-in.</p>
        <div id="user-list" class="user-list">${users.map(rowHtml).join("")}</div>
        <details class="card fold" id="add-user-fold">
          <summary>${icon("plus")}<span>Add a teacher</span>${icon("down", "ico-sm fold-caret")}</summary>
          <form id="add" class="fold-body form-grid">
            <label for="uname">Name<input id="uname" placeholder="Sophea" required /></label>
            <label for="uemail">Email<input id="uemail" type="email" placeholder="sophea@eden.gtis" required /></label>
            <label for="upass">First passcode<input id="upass" placeholder="at least 4 characters" required /></label>
            <label for="urole">Role
              <select id="urole">
                <option value="teacher" selected>Teacher</option>
                <option value="lead">Lead</option>
              </select></label>
            <label for="uposture" class="span-2">Coaching level (optional)
              <select id="uposture">
                <option value="" selected>—</option>
                <option value="junior">Junior — more guidance</option>
                <option value="regular">Regular</option>
                <option value="senior">Senior — light touch</option>
              </select></label>
            <p class="hint span-2">Tell the teacher their email and first passcode. They can change the passcode in Settings.</p>
            <div class="form-actions span-2"><button class="btn" type="submit">Add teacher</button></div>
          </form>
        </details>
      </section>

      <section class="setup-sec" id="classes">
        <div class="setup-head"><h2>Classes</h2></div>
        <p class="hint">Every class, its teacher and its periods per week.</p>
        <div id="class-manager"><p class="muted">Loading classes…</p></div>
      </section>

      <section class="setup-sec" id="year">
        <div class="setup-head"><h2>School year</h2></div>
        <p class="hint">The three terms the calendar and every "current week" run on. Type the dates from the school's own calendar; overlapping terms are refused.</p>
        <p class="hint" id="drift-line">${drift.drifted} of ${drift.total} stored deadlines differ from the calendar
          ${drift.drifted > 0 ? `· <a href="#" id="drift-recompute">Recompute</a>` : ""}</p>
        <div id="term-list" class="term-list">${terms.map(termRow).join("")}</div>
        <details class="card fold" id="add-term-fold">
          <summary>${icon("plus")}<span>Add a term</span>${icon("down", "ico-sm fold-caret")}</summary>
          <form id="add-term" class="fold-body">
            <div class="form-grid">
              <label>Term name<input name="name" required placeholder="AY2026/27 · Term 1" /></label>
              <label>Year label<input name="ay" placeholder="AY2026/27" /></label>
              <label>Starts<input type="date" name="start_date" required aria-label="Start date" /></label>
              <label>Ends<input type="date" name="end_date" required aria-label="End date" /></label>
              <label>Weeks<input type="number" name="week_count" value="13" min="1" max="20" required aria-label="Week count" /></label>
            </div>
            <div class="form-actions"><button class="btn" type="submit">Add term</button></div>
          </form>
        </details>
        <div class="setup-links">
          <a class="panel-row" data-link href="/calendar">${icon("calendar")}<span>School calendar — add or move events</span>${icon("chevron", "ico-sm")}</a>
          <a class="panel-row" data-link href="/calendar/import">${icon("sheet")}<span>Import the school calendar from Excel</span>${icon("chevron", "ico-sm")}</a>
        </div>
      </section>

      <section class="setup-sec" id="printing">
        <div class="setup-head"><h2>Printing</h2></div>
        <p class="hint">The school name and logo at the top of every new printed test or worksheet. A teacher can still change them on one paper.</p>
        <form id="school-header" class="card form-card">
          <div class="form-grid">
            <label for="shen">School name (English)<input id="shen" value="${esc(schoolNameEn)}" placeholder="Glad Tidings International School" /></label>
            <label for="shkm">School name (Khmer)<input id="shkm" lang="km" value="${esc(schoolNameKm)}" placeholder="សាលាភ្លើងអំណោយ" /></label>
            <label for="shlogo" class="span-2">Logo (optional, kept small)<input id="shlogo" type="file" accept="image/png,image/jpeg" /></label>
          </div>
          ${schoolLogo ? `<img src="${esc(schoolLogo)}" alt="Current logo" style="max-width:120px;display:block;margin:6px 0;border-radius:8px" />` : ""}
          <div class="form-actions"><button class="btn secondary" type="submit">Save school header</button></div>
        </form>
      </section>

      <section class="setup-sec" id="ai">
        <div class="setup-head"><h2>AI</h2></div>
        <p class="hint">Usually leave this as it is. It chooses the model that writes the private English feedback notes; a change takes effect within a few minutes.</p>
        <form id="ai-settings" class="card form-card">
          <label for="t2model">Feedback model<input id="t2model" placeholder="gemini-2.5-flash (default)" value="${esc(tier2Model)}" /></label>
          <div class="form-actions"><button class="btn secondary" type="submit">Save AI settings</button></div>
        </form>
      </section>`;
    flash = "";
    wire();
  }

  function wire(): void {
    const err = app.querySelector<HTMLElement>("#err")!;
    // The class manager loads its own data (all classes + all accounts) and saves itself.
    const cm = app.querySelector<HTMLElement>("#class-manager");
    if (cm) classesReady = mountClassManager(cm).catch((ex) => { cm.innerHTML = `<p class="error">${esc((ex as Error).message)}</p>`; });
    // The section bar scrolls within the page (no route change).
    app.querySelectorAll<HTMLAnchorElement>("[data-jump]").forEach((a) => a.addEventListener("click", (e) => {
      e.preventDefault();
      app.querySelector(`#${a.dataset.jump}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }));
    const showErr = (m: string): void => { err.textContent = m; err.hidden = false; };
    const val = (sel: string): string => (app.querySelector(sel) as HTMLInputElement | HTMLSelectElement).value.trim();

    app.querySelector<HTMLFormElement>("#add")!.addEventListener("submit", async (e) => {
      e.preventDefault();
      err.hidden = true;
      const name = val("#uname");
      const email = val("#uemail");
      const passcode = val("#upass");
      const auth_role = val("#urole");
      const coaching_posture = val("#uposture") || null;
      if (!name || !email || !passcode) return;
      try {
        await api("/api/admin/users", {
          method: "POST",
          body: JSON.stringify({ name, email, passcode, auth_role, coaching_posture }),
        });
        flash = `Added ${name}.`;
        await load();
      } catch (ex) {
        showErr((ex as Error).message);
      }
    });

    // School header defaults (P2b) — three settings keys, saved together as one flash message.
    app.querySelector<HTMLFormElement>("#school-header")!.addEventListener("submit", async (e) => {
      e.preventDefault();
      err.hidden = true;
      const en = val("#shen");
      const km = val("#shkm");
      const logoFile = (app.querySelector("#shlogo") as HTMLInputElement).files?.[0];
      try {
        await api("/api/admin/settings", { method: "PATCH", body: JSON.stringify({ key: "school_name_en", value: en }) });
        await api("/api/admin/settings", { method: "PATCH", body: JSON.stringify({ key: "school_name_km", value: km }) });
        if (logoFile) {
          const dataUrl = await fileToCappedDataUrl(logoFile);
          await api("/api/admin/settings", { method: "PATCH", body: JSON.stringify({ key: "school_logo", value: dataUrl }) });
        }
        flash = "School header saved.";
        await load();
      } catch (ex) {
        showErr((ex as Error).message);
      }
    });

    // AI settings — one whitelisted key for now (tier2_model); the Worker re-reads it each tick.
    app.querySelector<HTMLFormElement>("#ai-settings")!.addEventListener("submit", async (e) => {
      e.preventDefault();
      err.hidden = true;
      const value = val("#t2model");
      if (!value) return;
      try {
        await api("/api/admin/settings", { method: "PATCH", body: JSON.stringify({ key: "tier2_model", value }) });
        flash = `Feedback model set to ${value}.`;
        await load();
      } catch (ex) {
        showErr((ex as Error).message);
      }
    });

    // Academic year (0018) — each term row is its own small form; "Add a term" is a second one.
    // Both send the same shape, so one submit handler covers both.
    const termBody = (form: HTMLFormElement): Record<string, unknown> => {
      const fd = new FormData(form);
      return {
        name: String(fd.get("name") ?? "").trim(),
        ay: String(fd.get("ay") ?? "").trim() || null,
        start_date: String(fd.get("start_date") ?? ""),
        end_date: String(fd.get("end_date") ?? ""),
        week_count: Number(fd.get("week_count")),
      };
    };
    app.querySelector<HTMLElement>("#term-list")!.addEventListener("submit", async (e) => {
      e.preventDefault();
      err.hidden = true;
      const form = e.target as HTMLFormElement;
      const id = form.closest<HTMLElement>(".term-row")!.dataset.id!;
      try {
        await api(`/api/terms/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(termBody(form)) });
        flash = "Term updated.";
        savedTermId = id;
        try {
          await load();
          await classesReady;
          app.querySelector<HTMLElement>(`.term-row[data-id="${CSS.escape(id)}"]`)?.scrollIntoView({ block: "center" });
        } finally {
          savedTermId = null;
        }
      } catch (ex) {
        showErr((ex as Error).message);
      }
    });
    app.querySelector<HTMLAnchorElement>("#drift-recompute")?.addEventListener("click", async (e) => {
      e.preventDefault();
      err.hidden = true;
      try {
        const r = await api<{ recomputed: number }>("/api/admin/deadline-drift/recompute", { method: "POST" });
        flash = `Recomputed ${r.recomputed} deadline${r.recomputed === 1 ? "" : "s"}.`;
        await load();
      } catch (ex) {
        showErr((ex as Error).message);
      }
    });

    app.querySelector<HTMLFormElement>("#add-term")!.addEventListener("submit", async (e) => {
      e.preventDefault();
      err.hidden = true;
      const form = e.target as HTMLFormElement;
      const body = termBody(form);
      if (!body.name || !body.start_date || !body.end_date) return;
      try {
        await api("/api/terms", { method: "POST", body: JSON.stringify(body) });
        flash = `Added ${String(body.name)}.`;
        await load();
      } catch (ex) {
        showErr((ex as Error).message);
      }
    });

    // Posture select (per row) — change saves immediately, like the project board's move control.
    app.querySelector<HTMLElement>("#user-list")!.addEventListener("change", async (e) => {
      const sel = (e.target as HTMLElement).closest('select[data-act="posture"]') as HTMLSelectElement | null;
      if (!sel) return;
      const row = sel.closest<HTMLElement>(".user-row")!;
      const id = row.dataset.id!;
      try {
        await api(`/api/admin/users/${encodeURIComponent(id)}`, {
          method: "PATCH",
          body: JSON.stringify({ coaching_posture: sel.value || null }),
        });
        flash = sel.value ? `Coaching level set to ${sel.value}.` : "Coaching level cleared.";
        await load();
      } catch (ex) { showErr((ex as Error).message); }
    });

    // One delegated handler for reset / toggle / role / save / cancel on the user rows.
    app.querySelector<HTMLElement>("#user-list")!.addEventListener("click", async (e) => {
      const a = (e.target as HTMLElement).closest("a[data-act]") as HTMLAnchorElement | null;
      if (!a) return;
      e.preventDefault();
      const row = a.closest<HTMLElement>(".user-row")!;
      const id = row.dataset.id!;
      const act = a.dataset.act;
      const active = row.dataset.active === "1";
      const role = row.dataset.role;

      if (act === "toggle") {
        try {
          await api(`/api/admin/users/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify({ active: !active }) });
          flash = active ? "Account deactivated." : "Account reactivated.";
          await load();
        } catch (ex) { showErr((ex as Error).message); }
        return;
      }

      if (act === "role") {
        const next = role === "lead" ? "teacher" : "lead";
        try {
          await api(`/api/admin/users/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify({ auth_role: next }) });
          flash = `Role changed to ${next}.`;
          await load();
        } catch (ex) { showErr((ex as Error).message); }
        return;
      }

      if (act === "reset") {
        // Swap the action row for an inline "set a new passcode" field (the Lead tells the teacher).
        const actions = a.closest<HTMLElement>(".row-actions")!;
        actions.innerHTML = `<input class="reset-input" placeholder="New passcode" style="flex:1;min-width:130px" />
          <a href="#" data-act="save" class="muted">Save</a>
          <a href="#" data-act="cancel" class="muted">Cancel</a>`;
        actions.querySelector<HTMLInputElement>(".reset-input")!.focus();
        return;
      }

      if (act === "cancel") { await load(); return; }

      if (act === "save") {
        const pass = row.querySelector<HTMLInputElement>(".reset-input")!.value.trim();
        if (!pass) { await load(); return; }
        try {
          await api(`/api/admin/users/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify({ passcode: pass }) });
          flash = "Passcode updated.";
          await load();
        } catch (ex) { showErr((ex as Error).message); }
      }
    });
  }

  await load();
  if (tab && SECTIONS.some((x) => x.id === tab)) {
    app.addEventListener("eden:mounted", () => app.querySelector(`#${tab}`)?.scrollIntoView({ block: "start" }), { once: true });
  }
}
